import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { dashboardPathForRole } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useBranding, brandingFallback } from "@/lib/branding";

export default function Register() {
    const [form, setForm] = useState({ name: "", email: "", password: "", role: "student" });
    const [submitting, setSubmitting] = useState(false);
    const { register } = useAuth();
    const branding = useBranding();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const u = await register(form);
            toast.success(`Welcome, ${u.name}!`);
            navigate(dashboardPathForRole(u.role));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Registration failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-[80vh] flex items-center justify-center px-6 py-12" data-testid="register-page">
            <Card className="w-full max-w-md border-border">
                <CardContent className="p-8">
                    <div className="flex items-center gap-3 mb-6">
                        <img src={branding.logo_url} className="h-10 w-10 rounded-full object-contain" alt="" onError={brandingFallback} />
                        <div>
                            <div className="font-display text-primary text-xl">Create account</div>
                            <div className="text-xs text-muted-foreground uppercase tracking-widest">
                                {branding.institution_name}
                            </div>
                        </div>
                    </div>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="name">Full name</Label>
                            <Input
                                id="name"
                                required
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                data-testid="register-name-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                required
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                data-testid="register-email-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password">Password</Label>
                            <PasswordInput
                                id="password"
                                required
                                minLength={6}
                                value={form.password}
                                onChange={(e) => setForm({ ...form, password: e.target.value })}
                                data-testid="register-password-input"
                            />
                        </div>
                        <Button
                            type="submit"
                            className="w-full bg-primary hover:bg-primary/90"
                            disabled={submitting}
                            data-testid="register-submit-button"
                        >
                            {submitting ? "Creating…" : "Create account"}
                        </Button>
                    </form>
                    <div className="mt-6 text-sm text-center text-muted-foreground">
                        Already enrolled?{" "}
                        <Link to="/login" className="text-primary font-medium" data-testid="go-login-link">
                            Sign in
                        </Link>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
